import React, { useState, useEffect, useRef } from 'react';
import axios from '../../utils/axios';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';
import {
  ExclamationTriangleIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const WASTE_CATEGORIES = [
  { value: 'broken', label: 'שבור/נזק פיזי', icon: '💥' },
  { value: 'lost', label: 'אבד', icon: '❓' },
  { value: 'expired', label: 'פג תוקף', icon: '⏰' },
  { value: 'damaged', label: 'פגום/לא ניתן למכירה', icon: '⚠️' },
  { value: 'defective', label: 'פגם יצרני', icon: '🔧' },
  { value: 'other', label: 'אחר', icon: '📝' }
];

const CreateWasteReportModal = ({ isOpen, onClose, onReportCreated }) => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  const [formData, setFormData] = useState({
    warehouse_type: 'b2c',
    quantity: 1,
    waste_category: 'broken',
    reason: ''
  });

  const [errors, setErrors] = useState({});
  const debounceTimer = useRef(null);

  // Search products
  useEffect(() => {
    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Don't search if less than 2 characters
    if (searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    // Debounce search
    debounceTimer.current = setTimeout(async () => {
      try {
        const response = await axios.get('/api/products/quick-search', {
          params: { q: searchTerm, limit: 10 }
        });
        setSearchResults(response.data.results || response.data.products || []);
      } catch (error) {
        console.error('Error searching products:', error);
        setSearchResults([]);
      }
    }, 300);

    // Cleanup
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchTerm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setSearchTerm('');
    setSearchResults([]);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedProduct) {
      newErrors.product = 'יש לבחור מוצר';
    }

    const qty = parseInt(formData.quantity);
    if (!qty || qty < 1) {
      newErrors.quantity = 'כמות חייבת להיות לפחות 1';
    }

    if (selectedProduct) {
      const availableStock = formData.warehouse_type === 'b2c' 
        ? selectedProduct.stock_b2c 
        : selectedProduct.stock_b2b;
      
      if (qty > availableStock) {
        newErrors.quantity = `מלאי זמין: ${availableStock}`;
      }
    }

    if (!formData.reason.trim()) {
      newErrors.reason = 'סיבה היא שדה חובה';
    } else if (formData.reason.trim().length < 10) {
      newErrors.reason = 'סיבה חייבת להכיל לפחות 10 תווים';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('נא למלא את כל השדות הנדרשים');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/api/waste-reports', {
        product_id: selectedProduct.id,
        warehouse_type: formData.warehouse_type,
        quantity: parseInt(formData.quantity),
        waste_category: formData.waste_category,
        reason: formData.reason.trim()
      });

      toast.success(`דוח בלאי נוצר בהצלחה. ${formData.quantity} יחידות הורדו מהמלאי`);
      
      if (onReportCreated) {
        onReportCreated(response.data.report);
      }

      // Reset form
      setSelectedProduct(null);
      setFormData({
        warehouse_type: 'b2c',
        quantity: 1,
        waste_category: 'broken',
        reason: ''
      });
      
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה ביצירת דוח בלאי');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedProduct(null);
      setSearchTerm('');
      setFormData({
        warehouse_type: 'b2c',
        quantity: 1,
        waste_category: 'broken',
        reason: ''
      });
      setErrors({});
      onClose();
    }
  };

  const availableStock = selectedProduct 
    ? (formData.warehouse_type === 'b2c' ? selectedProduct.stock_b2c : selectedProduct.stock_b2b)
    : 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="דוח בלאי חדש"
      size="lg"
      preventClose={loading}
    >
      <form onSubmit={handleSubmit} className="space-y-6" dir="rtl">
        {/* Product Search */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            מוצר <span className="text-red-500">*</span>
          </label>
          
          {selectedProduct ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
              <div>
                <div className="font-semibold text-gray-900">{selectedProduct.name}</div>
                <div className="text-sm text-gray-600">מק"ט: {selectedProduct.sku}</div>
                <div className="text-xs text-gray-500 mt-1">
                  מלאי B2C: {selectedProduct.stock_b2c || 0} | מלאי B2B: {selectedProduct.stock_b2b || 0}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedProduct(null)}
                className="text-red-600 hover:text-red-700 text-sm font-medium"
              >
                שנה
              </button>
            </div>
          ) : (
            <div className="relative">
              <MagnifyingGlassIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="חפש מוצר לפי שם או מק״ט..."
                className={`w-full pr-10 pl-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                  errors.product ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              
              {searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                  {searchResults.map(product => (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => handleProductSelect(product)}
                      className="w-full px-4 py-3 text-right hover:bg-primary-50 transition-colors border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium text-gray-900">{product.name}</div>
                      <div className="text-sm text-gray-600">מק"ט: {product.sku}</div>
                      <div className="text-xs text-gray-500">
                        מלאי: B2C {product.stock_b2c || 0} | B2B {product.stock_b2b || 0}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {errors.product && <p className="mt-1 text-sm text-red-600">{errors.product}</p>}
        </div>

        {/* Warehouse Type */}
        {selectedProduct && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              סוג מלאי <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, warehouse_type: 'b2c' }))}
                className={`p-4 border-2 rounded-lg transition-all ${
                  formData.warehouse_type === 'b2c'
                    ? 'border-primary-600 bg-primary-50 text-primary-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="font-semibold">B2C (קמעונאי)</div>
                <div className="text-sm">מלאי זמין: {selectedProduct.stock_b2c || 0}</div>
              </button>
              
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, warehouse_type: 'b2b' }))}
                className={`p-4 border-2 rounded-lg transition-all ${
                  formData.warehouse_type === 'b2b'
                    ? 'border-primary-600 bg-primary-50 text-primary-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="font-semibold">B2B (סיטונאי)</div>
                <div className="text-sm">מלאי זמין: {selectedProduct.stock_b2b || 0}</div>
              </button>
            </div>
          </div>
        )}

        {/* Quantity */}
        {selectedProduct && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              כמות <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleInputChange}
              min="1"
              max={availableStock}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                errors.quantity ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            <p className="mt-1 text-sm text-gray-600">
              מלאי זמין: {availableStock}
            </p>
            {errors.quantity && <p className="mt-1 text-sm text-red-600">{errors.quantity}</p>}
          </div>
        )}

        {/* Waste Category */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            קטגוריית בלאי <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            {WASTE_CATEGORIES.map(category => (
              <button
                key={category.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, waste_category: category.value }))}
                className={`p-3 border-2 rounded-lg text-right transition-all ${
                  formData.waste_category === category.value
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">{category.icon}</span>
                  <span className="font-medium text-sm">{category.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Reason */}
        <div>
          <label htmlFor="waste-reason" className="block text-sm font-semibold text-gray-700 mb-2">
            סיבה מפורטת <span className="text-red-500">*</span>
          </label>
          <textarea
            id="waste-reason"
            name="reason"
            value={formData.reason}
            onChange={handleInputChange}
            rows="4"
            maxLength="200"
            placeholder="תאר בפירוט את הסיבה לבלאי (מינימום 10 תווים)..."
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
              errors.reason ? 'border-red-500' : 'border-gray-300'
            }`}
            autoComplete="off"
          />
          {errors.reason && (
            <p className="mt-1 text-sm text-red-600">{errors.reason}</p>
          )}
        </div>

        {/* Warning */}
        {selectedProduct && formData.quantity > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-semibold mb-1">שים לב:</p>
                <p>
                  פעולה זו תוריד <strong>{formData.quantity} יחידות</strong> ממלאי {formData.warehouse_type === 'b2c' ? 'B2C' : 'B2B'} של המוצר.
                  <br />
                  מלאי חדש יהיה: <strong>{availableStock - formData.quantity}</strong>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 font-medium"
          >
            ביטול
          </button>
          
          <button
            type="submit"
            disabled={loading || !selectedProduct}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2 font-semibold"
          >
            {loading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            )}
            <ExclamationTriangleIcon className="h-5 w-5" />
            <span>דווח בלאי</span>
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateWasteReportModal;
